import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, CircularProgress,
  List, ListItem, ListItemButton, ListItemText, Chip, Grid, Button
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { searchHousePractice } from '../services/api';

const THEME_COLOR = '#ff9800';

// Render a single House Practice chapter (mirror of StandingOrderContent)
function HousePracticeContent({ chapter, isFirst, themeColor = THEME_COLOR }) {
  return (
    <Box
      id={`hp-${chapter.chapter_number}`}
      sx={{
        scrollMarginTop: '80px',
        mb: 3,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {isFirst && <MenuBookIcon sx={{ color: themeColor, fontSize: 28 }} />}
        <Chip
          label={`Chapter ${chapter.chapter_number}`}
          size="small"
          sx={{ bgcolor: themeColor, color: 'white' }}
        />
        <Typography variant={isFirst ? 'h5' : 'h6'} sx={{ fontWeight: 600, color: themeColor }}>
          {chapter.chapter_title || 'Untitled'}
        </Typography>
      </Box>

      {chapter.edition && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={chapter.edition} variant="outlined" size="small" />
        </Box>
      )}

      {/* Body — house_practice docs ship plain text in `content`, no HTML variant */}
      {chapter.content_html ? (
        <Box
          sx={{
            '& p': { mb: 2, lineHeight: 1.8 },
            '& ol': { pl: 3, mb: 2 },
            '& ul': { pl: 3, mb: 2 },
            '& li': { mb: 1, lineHeight: 1.7 }
          }}
          dangerouslySetInnerHTML={{ __html: chapter.content_html }}
        />
      ) : (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
          {chapter.content}
        </Typography>
      )}

      {/* Source link */}
      {chapter.source_url && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            href={chapter.source_url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
            sx={{ color: themeColor }}
          >
            View on APH Website
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default function HousePracticeSearch() {
  const [chapters, setChapters] = useState([]);    // full chapter docs, ordered by chapter_number
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const contentRef = useRef(null);

  // Load all 21 chapters on mount — they're cheap (~14k chars each) and the page
  // is a single document, not a search. One call, sorted by chapter_number.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // `sort=chapter` orders by chapter_number asc; per_page=100 covers all 21
        const data = await searchHousePractice('*', 1, 100);
        if (cancelled) return;
        const sorted = (data.hits || []).slice().sort((a, b) => {
          const ac = a.chapter_number || 0;
          const bc = b.chapter_number || 0;
          return ac - bc;
        });
        setChapters(sorted);
        if (sorted.length > 0) {
          setActiveChapter(sorted[0].chapter_number);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load House Practice:', err);
          setError(err.message || 'Failed to load House Practice.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleChapterClick = useCallback((chapterNumber) => {
    setActiveChapter(chapterNumber);
    // Scroll the anchor for that chapter into view in the body pane
    const el = document.getElementById(`hp-${chapterNumber}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <MenuBookIcon sx={{ color: THEME_COLOR, fontSize: 32 }} />
        <Typography variant="h4">
          House of Representatives Practice
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {!loading && !error && chapters.length > 0 && (
        <Grid container spacing={3}>
          {/* Table of Contents Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 16,
                maxHeight: 'calc(100vh - 200px)',
                overflow: 'auto'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Table of Contents
              </Typography>
              <List dense disablePadding>
                {chapters.map((ch) => {
                  const isCurrent = activeChapter === ch.chapter_number;
                  return (
                    <ListItem key={ch.chapter_number} disablePadding>
                      <ListItemButton
                        onClick={() => handleChapterClick(ch.chapter_number)}
                        selected={isCurrent}
                        sx={{
                          py: 0.5,
                          borderRadius: 1,
                          borderLeft: isCurrent
                            ? `3px solid ${THEME_COLOR}`
                            : '3px solid transparent',
                          bgcolor: isCurrent ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                          '&.Mui-selected': { bgcolor: 'rgba(255, 152, 0, 0.08)' },
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isCurrent ? 600 : 500,
                                fontSize: '0.85rem',
                                color: isCurrent ? THEME_COLOR : 'text.primary'
                              }}
                            >
                              Ch. {ch.chapter_number}: {ch.chapter_title || 'Untitled'}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>

          {/* Main Content — all 21 chapters rendered in order; TOC scrolls to anchor */}
          <Grid item xs={12} md={8}>
            <Paper
              ref={contentRef}
              elevation={0}
              sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
            >
              {chapters.map((ch, idx) => (
                <HousePracticeContent
                  key={ch.id || ch.provision_id || ch.chapter_number}
                  chapter={ch}
                  isFirst={idx === 0}
                  themeColor={THEME_COLOR}
                />
              ))}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
